import { jwtDecode } from 'jwt-decode';

import { keyConst } from '@/constants';

import { TOKEN_REFRESH_MS } from '@/enum/token-refresh.enum';

interface ITokenExpiryPayload {
  exp?: number;
  rememberMe?: boolean;
}

interface ITokenRefreshConfig {
  refresh: () => Promise<string | null>;
  onTokenUpdated?: (token: string) => void;
}

let refreshTimerId: ReturnType<typeof setTimeout> | null = null;
let refreshHandler: ITokenRefreshConfig['refresh'] | null = null;
let onTokenUpdated: ITokenRefreshConfig['onTokenUpdated'] | null = null;

const clearRefreshTimer = (): void => {
  if (refreshTimerId !== null) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
};

const getTokenExpiryMs = (token: string): number | null => {
  try {
    const decoded = jwtDecode<ITokenExpiryPayload>(token);
    if (!decoded.exp) return null;
    return decoded.exp * 1000;
  } catch {
    return null;
  }
};

const isRememberMeSession = (token: string): boolean => {
  try {
    return Boolean(jwtDecode<ITokenExpiryPayload>(token).rememberMe);
  } catch {
    return false;
  }
};

const scheduleAt = (targetTimeMs: number, callback: () => void): void => {
  clearRefreshTimer();
  const delay = Math.max(0, targetTimeMs - Date.now());

  if (delay > TOKEN_REFRESH_MS.MAX_TIMEOUT) {
    refreshTimerId = setTimeout(() => {
      scheduleAt(targetTimeMs, callback);
    }, TOKEN_REFRESH_MS.MAX_TIMEOUT);
    return;
  }

  refreshTimerId = setTimeout(callback, delay);
};

const runScheduledRefresh = async (): Promise<void> => {
  if (!refreshHandler) return;

  try {
    const nextToken = await refreshHandler();
    if (!nextToken) {
      stopTokenRefresh();
      return;
    }

    onTokenUpdated?.(nextToken);
    scheduleTokenRefresh(nextToken);
  } catch {
    stopTokenRefresh();
  }
};

export const shouldAttemptRefresh = (): boolean => {
  if (localStorage.getItem(keyConst.rememberMe) === 'true') return true;
  const token = localStorage.getItem(keyConst.userToken);
  return Boolean(token && isRememberMeSession(token));
};

export const scheduleTokenRefresh = (token: string | null | undefined): void => {
  clearRefreshTimer();

  if (!token || !isRememberMeSession(token)) return;

  const expiryMs = getTokenExpiryMs(token);
  if (!expiryMs) return;

  scheduleAt(expiryMs - TOKEN_REFRESH_MS.BEFORE_EXPIRY, () => {
    void runScheduledRefresh();
  });
};

export const stopTokenRefresh = (): void => {
  clearRefreshTimer();
};

export const configureTokenRefresh = (options: ITokenRefreshConfig): void => {
  refreshHandler = options.refresh;
  onTokenUpdated = options.onTokenUpdated ?? null;
};

export const syncTokenRefreshFromStorage = (): void => {
  const token = localStorage.getItem(keyConst.userToken);
  if (token) {
    scheduleTokenRefresh(token);
    return;
  }
  if (localStorage.getItem(keyConst.rememberMe) === 'true') {
    void runScheduledRefresh();
  }
};

export const notifyAccessTokenUpdated = (token: string): void => {
  onTokenUpdated?.(token);
  scheduleTokenRefresh(token);
};
